import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useLang } from '../LanguageContext';
import DeviceCard from '../components/DeviceCard';
import WaterStatusCard from '../components/WaterStatusCard';
import SoilDeviceCard from '../components/SoilDeviceCard';
import { isDeviceOnline, formatDeviceTimestamp } from '../utils/timeUtils';
import { useAuth } from '../context/AuthContext';
import { authFetch } from '../utils/authFetch';
import { isRateLimitBackoffActive } from '../utils/rateLimitBackoff';
import { isGlobalRole } from '../utils/roles';
import {
  mapApiActiveDeviceToCardDevice,
  normalizeThresholds,
} from '../utils/mapApiActiveDeviceToCardDevice';

const OFFLINE_THRESHOLD_MS = 5 * 60 * 1000;
const LEGACY_POLL_INTERVAL_MS = 3 * 60 * 1000;

export default function DevicesPage() {
  const { t, lang } = useLang();
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const { token, logout, user } = useAuth();
  const navigate = useNavigate();
  if (!token) return <Navigate to="/login" replace />;
  const [devices, setDevices] = useState([]);
  const [soilDevices, setSoilDevices] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showOffline, setShowOffline] = useState(false);
  const [adminFarms, setAdminFarms] = useState([]);
  const [bindingModalOpen, setBindingModalOpen] = useState(false);
  const [bindingDevice, setBindingDevice] = useState(null);
  const [bindingFarmId, setBindingFarmId] = useState('');
  const [bindingRoomId, setBindingRoomId] = useState('');
  const [bindingRooms, setBindingRooms] = useState([]);
  const [bindingLoadingRooms, setBindingLoadingRooms] = useState(false);
  const [bindingSaving, setBindingSaving] = useState(false);
  const [bindingError, setBindingError] = useState('');

  const checkOnlineStatus = useCallback((deviceLike = {}) => {
    if (deviceLike.deviceStatus && Object.prototype.hasOwnProperty.call(deviceLike.deviceStatus, 'status')) {
      const explicitStatus = String(deviceLike.deviceStatus.status || '').trim().toLowerCase();
      if (explicitStatus === 'online' || explicitStatus === 'idle') return true;
      if (explicitStatus === 'offline') return false;
    }
    const mqttEnabled =
      deviceLike.mqttEnabled === true ||
      deviceLike.meta?.mqttEnabled === true;
    if (mqttEnabled) {
      return String(deviceLike.status || '').trim().toLowerCase() !== 'offline';
    }
    const ts = deviceLike.timestamp || deviceLike.lastUpdated || deviceLike.lastSeenAtIso;
    if (!ts) return false;
    const asDate = new Date(ts);
    if (!Number.isFinite(asDate.getTime())) {
      return isDeviceOnline(ts);
    }
    return Date.now() - asDate.getTime() <= OFFLINE_THRESHOLD_MS;
  }, []);

  const handleUnauthorized = useCallback(() => {
    logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await authFetch(
        '/api/devices/active',
        {},
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      const mapped = list.map((d) => mapApiActiveDeviceToCardDevice(d, checkOnlineStatus));
      setDevices(mapped);
    } catch (err) {
      console.error('Failed to fetch devices', err);
    }

    try {
      const res = await authFetch(
        '/api/sensor-data/soil7in1/devices',
        {},
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      const list = await res.json();
      const mapped = Array.isArray(list)
        ? list.map((d) => ({
            id: d.deviceId,
            name: d.name,
            status:
              d.ts && Date.now() - new Date(d.ts).getTime() <= 10 * 60 * 1000
                ? 'online'
                : 'offline',
            lastUpdated: d.ts,
            controller: 'soil',
            controllerType: 'soil',
          }))
        : [];
      setSoilDevices(mapped);
    } catch (err) {
      console.error('Failed to fetch soil devices', err);
    }
  }, [checkOnlineStatus, handleUnauthorized, token]);

  const refreshDevicesList = useCallback(async () => {
    try {
      const res = await authFetch(
        '/api/devices/refresh',
        { method: 'POST' },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) return;
      fetchDevices();
    } catch (err) {
      console.error('Failed to refresh devices list', err);
    }
  }, [fetchDevices, handleUnauthorized, token]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  useEffect(() => {
    if (!isGlobalRole(user?.role) || !token) {
      setAdminFarms([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/farms', {}, { token, onUnauthorized: handleUnauthorized });
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (!cancelled) setAdminFarms(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setAdminFarms([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.role, token, handleUnauthorized]);

  const canMoveDevicesAcrossFarms = isGlobalRole(user?.role);
  const canManageBindings = isGlobalRole(user?.role);

  const loadRoomsForFarm = useCallback(async (farmId) => {
    if (!farmId || !token) {
      setBindingRooms([]);
      return;
    }
    setBindingLoadingRooms(true);
    try {
      const res = await authFetch(
        `/api/rooms?farmId=${encodeURIComponent(farmId)}`,
        {},
        {
          token,
          onUnauthorized: handleUnauthorized,
          farmContextId: farmId,
        }
      );
      if (!res.ok) {
        setBindingRooms([]);
        return;
      }
      const data = await res.json();
      setBindingRooms(Array.isArray(data) ? data : []);
    } catch (_) {
      setBindingRooms([]);
    } finally {
      setBindingLoadingRooms(false);
    }
  }, [token, handleUnauthorized]);

  const openBindingModal = useCallback((device) => {
    const currentFarm = device?.farmId ? String(device.farmId) : '';
    const currentRoom = device?.roomId ? String(device.roomId) : '';
    setBindingDevice(device || null);
    setBindingFarmId(currentFarm);
    setBindingRoomId(currentRoom);
    setBindingError('');
    setBindingModalOpen(true);
    if (currentFarm) {
      loadRoomsForFarm(currentFarm);
    } else {
      setBindingRooms([]);
    }
  }, [loadRoomsForFarm]);

  const closeBindingModal = useCallback(() => {
    setBindingModalOpen(false);
    setBindingDevice(null);
    setBindingFarmId('');
    setBindingRoomId('');
    setBindingRooms([]);
    setBindingError('');
  }, []);

  const handleUnbindDevice = useCallback(async (device) => {
    const did = String(device?.deviceId || device?.id || '');
    if (!did || !token) return;
    const confirmed = window.confirm('Отвязать устройство от фермы и комнаты?');
    if (!confirmed) return;
    try {
      const res = await authFetch(
        `/api/devices/unbind/${encodeURIComponent(did)}`,
        { method: 'PATCH' },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        alert(payload?.error || 'Не удалось отвязать устройство');
        return;
      }
      window.dispatchEvent(new Event('shefa-unassigned-refresh'));
      await fetchDevices();
    } catch (_) {
      alert('Не удалось отвязать устройство');
    }
  }, [token, handleUnauthorized, fetchDevices]);

  const saveBinding = useCallback(async () => {
    if (!bindingDevice || !bindingFarmId || !token) {
      setBindingError('Выберите ферму');
      return;
    }
    setBindingSaving(true);
    setBindingError('');
    try {
      const targetDeviceId = encodeURIComponent(bindingDevice.id || bindingDevice.deviceId);
      const farmRes = await authFetch(
        `/api/admin/devices/${targetDeviceId}/farm`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ farmId: bindingFarmId }),
        },
        { token, onUnauthorized: handleUnauthorized }
      );
      if (!farmRes.ok) {
        const payload = await farmRes.json().catch(() => ({}));
        setBindingError(payload?.error || 'Не удалось сохранить ферму');
        return;
      }
      const roomRes = await authFetch(
        `/api/device/${targetDeviceId}/room`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ roomId: bindingRoomId || null }),
        },
        { token, onUnauthorized: handleUnauthorized, farmContextId: bindingFarmId }
      );
      if (!roomRes.ok) {
        const payload = await roomRes.json().catch(() => ({}));
        setBindingError(payload?.error || 'Не удалось сохранить комнату');
        return;
      }
      closeBindingModal();
      await fetchDevices();
    } catch (_) {
      setBindingError('Не удалось сохранить привязку');
    } finally {
      setBindingSaving(false);
    }
  }, [
    bindingDevice,
    bindingFarmId,
    bindingRoomId,
    token,
    handleUnauthorized,
    fetchDevices,
    closeBindingModal,
  ]);

  useEffect(() => {
    const hasLegacyDevices = devices.some(
      (device) => device.controller !== 'soil-7in1' && !device.mqttEnabled
    );
    if (!hasLegacyDevices) return undefined;
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (isRateLimitBackoffActive()) return;
      fetchDevices();
    }, LEGACY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [devices, fetchDevices]);

  const refreshDevice = async (id) => {
    try {
      const current = devices.find((d) => d.id === id || d.deviceId === id);
      const endpoint =
        current?.controller === 'water'
          ? `/api/sensor-data/water/latest/${id}`
          : `/api/sensor-data/climate/latest/${id}`;
      const res = await fetch(endpoint);
      if (!res.ok) return;
      const device = await res.json();
      if (!device) return;

      // Защита от отсутствующих данных
      const thresholds = device.thresholds || {};
      const sockets = device.socketStates || device.sockets || {};
      const latestDeviceStatuses =
        device.deviceStatuses || device.meta?.device_statuses || current?.deviceStatuses || {};

      function safeField(val) {
        if (val === undefined || val === null) return null;
        if (typeof val === 'string') {
          const arr = val
            .split(',')
            .map((x) => parseFloat(x))
            .filter((x) => !isNaN(x));
          return arr.length > 0 ? arr : null;
        }
        return Array.isArray(val) ? val : [val];
      }

      const normalizedThresholds = normalizeThresholds(thresholds, current?.thresholds || {});

      const parsed =
        current?.controller === 'water'
          ? {
              ph: device.ph != null ? `${Number(device.ph).toFixed(2)}` : t('noData'),
              ec: device.ec != null ? `${Number(device.ec).toFixed(3)} mS/cm` : t('noData'),
              orp: device.orp != null ? `${Number(device.orp).toFixed(0)} mV` : t('noData'),
              ecTemp:
                (device.ecTemp ?? device.temp ?? device.temperature) != null
                  ? `${Number(device.ecTemp ?? device.temp ?? device.temperature).toFixed(1)} °C`
                  : t('noData'),
            }
          : {
              temperature:
                device.temperature != null
                  ? `${Number(device.temperature).toFixed(2)} °C`
                  : t('noData'),
              humidity:
                device.humidity != null
                  ? `${Number(device.humidity).toFixed(2)} %`
                  : t('noData'),
              co2: device.co2 != null ? `${device.co2} ppm` : t('noData'),
              vpd:
                device.vpd != null ? `${Number(device.vpd).toFixed(2)} kPa` : t('noData'),
              light: device.light ?? null,
              lightStatus: device.lightStatus ?? t('noData'),
              sockets: {
                temperature: sockets.temperature ?? null,
                humidity: sockets.humidity ?? null,
                co2: sockets.co2 ?? null,
              },
              thresholds: {
                day: {
                  temperature: normalizedThresholds.day.temperature,
                  humidity: normalizedThresholds.day.humidity,
                  co2: normalizedThresholds.day.co2,
                },
                night: {
                  temperature: normalizedThresholds.night.temperature,
                  humidity: normalizedThresholds.night.humidity,
                  co2: normalizedThresholds.night.co2,
                },
              },
            };

      device.parsedReadings = parsed;
      device.thresholds = normalizedThresholds;
      device.lastUpdated = device.timestamp;
      device.timestamp = device.timestamp;
      device.controller = current?.controller || device.controller || 'climate';
      device.ecTemp = device.ecTemp ?? device.temp ?? device.temperature;
      device.telegramEnabled = device.telegramEnabled ?? current?.telegramEnabled ?? device.alertsEnabled ?? true;
      device.powerAlerts = device.powerAlerts ?? current?.powerAlerts ?? device.notifyOnOffline ?? true;
      device.thresholdAlerts = device.thresholdAlerts ?? current?.thresholdAlerts ?? device.alertsEnabled ?? true;
      device.trendAlerts = device.trendAlerts ?? current?.trendAlerts ?? device.trendAlertsEnabled ?? false;
      device.notificationDelivery = device.notificationDelivery ?? current?.notificationDelivery ?? null;
      device.deviceStatuses = device.deviceStatuses ?? device.meta?.device_statuses ?? current?.deviceStatuses ?? {};
      device.co2Warning = device.co2Warning ?? current?.co2Warning ?? null;
      device.socketStates = device.socketStates ?? current?.socketStates ?? sockets ?? {};
      device.sockets = device.sockets ?? current?.sockets ?? sockets ?? {};
      device.meta = device.meta ?? current?.meta ?? {};
      device.mqttEnabled = device.mqttEnabled ?? current?.mqttEnabled ?? device.meta?.mqttEnabled ?? false;
      if ((!device.socketStates || !Object.keys(device.socketStates).length) && latestDeviceStatuses) {
        device.socketStates = {
          temperature: latestDeviceStatuses.ac ?? null,
          humidity: latestDeviceStatuses.dehumidifier ?? null,
          co2: latestDeviceStatuses.co2 ?? null,
        };
      }
      device.light = device.light ?? current?.light ?? null;
      device.rA = device.rA ?? current?.rA ?? null;
      device.rD = device.rD ?? current?.rD ?? null;
      device.rC = device.rC ?? current?.rC ?? null;
      device.rL = device.rL ?? current?.rL ?? null;
      device.rb = device.rb ?? current?.rb ?? null;
      device.relays = device.relays ?? device.meta?.relays ?? current?.relays ?? {};
      device.status = checkOnlineStatus(device) ? 'online' : 'offline';

      setDevices((prev) =>
        prev.map((d) =>
          d.id === id || d.deviceId === id
            ? { ...d, ...device }
            : d
        )
      );
    } catch (err) {
      console.error('Failed to refresh device', err);
    }
  };

  const formatDate = (str) => {
    if (!str) return '-';
    const locale = lang === 'he' ? 'he-IL' : lang === 'es' ? 'es-ES' : lang;
    return formatDeviceTimestamp(str, locale);
  };

  const climateDevices = devices.filter((d) => d.controller !== 'water');
  const waterDevices = devices.filter((d) => d.controller === 'water');
  const onlineDevices = [...climateDevices, ...soilDevices].filter(
    (d) => d.status !== 'offline'
  );
  const offlineDevices = [...climateDevices, ...soilDevices].filter(
    (d) => d.status === 'offline'
  );
  const renderDeviceCard = (device) => {
    if (device.controller === 'soil-7in1') {
      return <SoilDeviceCard device={device} t={t} lang={lang} />;
    }
    if (device.controller === 'water') {
      return <WaterStatusCard key={device.deviceId} device={device} lang={lang} t={t} onRefresh={refreshDevice} />;
    }
    return (
      <div className="w-100 min-w-0 d-flex flex-column">
        <DeviceCard
          device={device}
          t={t}
          lang={lang}
          formatDate={formatDate}
          onRefresh={refreshDevice}
          showFarmTransfer={canMoveDevicesAcrossFarms}
          allowFarmUnassign={isGlobalRole(user?.role)}
          farmsList={adminFarms}
          onDeviceFarmMoved={fetchDevices}
        />
        {canManageBindings ? (
          <div className="d-flex gap-2 mt-2 px-1">
            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleUnbindDevice(device)}>
              Отвязать устройство
            </button>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openBindingModal(device)}>
              Редактировать привязку
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const getDeviceColumnClass = (device) => {
    if (device.controller === 'climate') {
      return 'col-12 mb-3';
    }
    if (device.controller === 'soil-7in1') {
      return 'col-12 col-md-6 col-lg-4 mb-3';
    }
    return 'col-12 col-md-6 col-lg-4 mb-3';
  };

  useEffect(() => {
    if (devices.length > 0 && onlineDevices.length === 0) {
      setShowOffline(true);
    }
  }, [devices]);

  return (
    <div className="container-fluid mt-3" dir={dir}>
      {devices.length === 0 && soilDevices.length === 0 && (
        <div className="alert alert-info">{t('noDevicesFound')}</div>
      )}
      <div className="d-flex align-items-center mb-3">
        <h1 className="me-3">{t('nav.devices')}</h1>
        <button className="btn btn-secondary" onClick={refreshDevicesList}>
          {t('refresh')}
        </button>
      </div>
      <div className="mb-3">
        <select
          className="form-select w-auto"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">{t('all')}</option>
          <option value="climate">{t('climate')}</option>
          <option value="water">{t('waterInAndOut')}</option>
        </select>
      </div>
      {(typeFilter === 'all' || typeFilter === 'climate') && (
        <>
          <div className="row mx-0">
            {onlineDevices.map((d) => (
              <div key={d.id} className={`${getDeviceColumnClass(d)} shefa-device-card-slot`}>
                {renderDeviceCard(d)}
              </div>
            ))}
          </div>
          {offlineDevices.length > 0 && (
            <div className="mt-3">
              <button
                className="btn btn-link"
                onClick={() => setShowOffline(!showOffline)}
              >
                {showOffline
                  ? t('hideOffline')
                  : t('showOffline', offlineDevices.length)}
              </button>
              {showOffline && (
                <div className="row mx-0 mt-2">
                  {offlineDevices.map((d) => (
                    <div key={d.id} className={getDeviceColumnClass(d)}>
                      {renderDeviceCard(d)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {(typeFilter === 'all' || typeFilter === 'water') && (
        <div className="mt-4">
          <h3 className="mb-3">{t('waterDevices')}</h3>
          <div className="row mx-0">
            {waterDevices.map((d) => (
              <div key={d.deviceId} className="col-12 col-md-6 col-lg-4 mb-3 shefa-device-card-slot">
                {renderDeviceCard(d)}
              </div>
            ))}
          </div>
        </div>
      )}
      {bindingModalOpen ? (
        <div className="modal d-block" tabIndex="-1" role="dialog" onClick={closeBindingModal}>
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Редактировать привязку</h5>
                <button type="button" className="btn-close" onClick={closeBindingModal} />
              </div>
              <div className="modal-body">
                {bindingError ? <div className="alert alert-warning py-2">{bindingError}</div> : null}
                <div className="mb-3">
                  <label className="form-label">Ферма</label>
                  <select
                    className="form-select"
                    value={bindingFarmId}
                    onChange={async (e) => {
                      const nextFarm = e.target.value;
                      setBindingFarmId(nextFarm);
                      setBindingRoomId('');
                      await loadRoomsForFarm(nextFarm);
                    }}
                  >
                    <option value="">Выберите ферму</option>
                    {adminFarms.map((farm) => (
                      <option key={String(farm._id)} value={String(farm._id)}>
                        {farm.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-1">
                  <label className="form-label">Комната</label>
                  <select
                    className="form-select"
                    value={bindingRoomId}
                    onChange={(e) => setBindingRoomId(e.target.value)}
                    disabled={!bindingFarmId || bindingLoadingRooms}
                  >
                    <option value="">Без комнаты</option>
                    {bindingRooms.map((room) => (
                      <option key={String(room._id)} value={String(room._id)}>
                        {room.name}
                      </option>
                    ))}
                  </select>
                  {bindingLoadingRooms ? <div className="form-text">Загружаем комнаты...</div> : null}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeBindingModal} disabled={bindingSaving}>
                  Отмена
                </button>
                <button type="button" className="btn btn-primary" onClick={saveBinding} disabled={bindingSaving}>
                  {bindingSaving ? 'Сохраняем...' : 'Сохранить'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
